import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEmpresa } from "@/contexts/EmpresaContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import {
  QrCode,
  Smartphone,
  Mail,
  PlugZap,
  Clock3,
  CheckCircle2,
  RefreshCcw,
} from "lucide-react";

type ConnectionStatus = "desconectado" | "aguardando_leitura" | "conectado";
type EvolutionConnectionState = "close" | "connecting" | "open" | "connected";

const EVOLUTION_STATE_TO_CONNECTION_STATUS: Record<EvolutionConnectionState, ConnectionStatus> = {
  close: "desconectado",
  connecting: "aguardando_leitura",
  open: "conectado",
  connected: "conectado",
};

const STORED_STATE_TO_CONNECTION_STATUS: Record<string, ConnectionStatus> = {
  close: "desconectado",
  connecting: "aguardando_leitura",
  open: "conectado",
  connected: "conectado",
};

const STATUS_CONFIG: Record<
  ConnectionStatus,
  { label: string; className: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  desconectado: {
    label: "Desconectado",
    className: "bg-slate-100 text-slate-700",
    Icon: PlugZap,
  },
  aguardando_leitura: {
    label: "Aguardando leitura do QR",
    className: "bg-amber-100 text-amber-800",
    Icon: Clock3,
  },
  conectado: {
    label: "Conectado",
    className: "bg-emerald-100 text-emerald-800",
    Icon: CheckCircle2,
  },
};

const formatDateTime = (isoDate: string | null) => {
  if (!isoDate) return "-";
  return new Date(isoDate).toLocaleString("pt-BR");
};

const CONNECTION_POLLING_INTERVAL_MS = 10_000;
const CONNECTION_POLLING_MAX_DURATION_MS = 30 * 60 * 1000;

const isEvolutionConnectionState = (value: unknown): value is EvolutionConnectionState => {
  return value === "close" || value === "connecting" || value === "open" || value === "connected";
};

const extractQrCodeFromResponse = (responseBody: unknown): string | null => {
  if (!responseBody) return null;

  if (typeof responseBody === "string") {
    return responseBody.trim() || null;
  }

  if (Array.isArray(responseBody)) {
    for (const item of responseBody) {
      const nested = extractQrCodeFromResponse(item);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  if (typeof responseBody !== "object") return null;

  const response = responseBody as Record<string, unknown>;
  const directKeys = ["qrCode", "qr_code", "qrcode", "qr", "base64", "base64Qr", "base64_qr"];

  for (const key of directKeys) {
    const value = response[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  const nestedKeys = ["data", "result", "payload"];
  for (const key of nestedKeys) {
    const nested = extractQrCodeFromResponse(response[key]);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const extractInstanceNameFromResponse = (responseBody: unknown): string | null => {
  if (!responseBody) return null;

  if (Array.isArray(responseBody)) {
    for (const item of responseBody) {
      const nested = extractInstanceNameFromResponse(item);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  if (typeof responseBody !== "object") return null;

  const response = responseBody as Record<string, unknown>;
  const directKeys = ["instanceName", "instance_name", "phone_number", "phoneNumber"];

  for (const key of directKeys) {
    const value = response[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  const nestedKeys = ["instance", "data", "result", "payload"];
  for (const key of nestedKeys) {
    const nested = extractInstanceNameFromResponse(response[key]);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const extractConnectionStateFromResponse = (responseBody: unknown): EvolutionConnectionState | null => {
  if (!responseBody) return null;

  if (Array.isArray(responseBody)) {
    for (const item of responseBody) {
      const nested = extractConnectionStateFromResponse(item);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  if (typeof responseBody !== "object") return null;

  const response = responseBody as Record<string, unknown>;

  if (isEvolutionConnectionState(response.state)) {
    return response.state;
  }

  const nestedKeys = ["instance", "data", "result", "payload"];
  for (const key of nestedKeys) {
    const nested = extractConnectionStateFromResponse(response[key]);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const extractSessionIdFromResponse = (responseBody: unknown): string | null => {
  if (!responseBody) return null;

  if (Array.isArray(responseBody)) {
    for (const item of responseBody) {
      const nested = extractSessionIdFromResponse(item);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  if (typeof responseBody !== "object") return null;

  const response = responseBody as Record<string, unknown>;
  const directKeys = ["sessionId", "session_id", "instanceId", "instance_id", "id"];

  for (const key of directKeys) {
    const value = response[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    if (typeof value === "number") {
      return String(value);
    }
  }

  const nestedKeys = ["data", "result", "payload"];
  for (const key of nestedKeys) {
    const nested = extractSessionIdFromResponse(response[key]);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const normalizeQrCodeValue = (qrValue: string) => {
  const trimmed = qrValue.trim();

  if (trimmed.startsWith("data:image")) {
    return trimmed;
  }

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  if (trimmed.startsWith("<svg")) {
    return `data:image/svg+xml;utf8,${encodeURIComponent(trimmed)}`;
  }

  const compact = trimmed.replace(/\s+/g, "");
  const looksLikeBase64 = /^[A-Za-z0-9+/=]+$/.test(compact) && compact.length > 120;
  if (looksLikeBase64) {
    return `data:image/png;base64,${compact}`;
  }

  return trimmed;
};

export default function AdminCommunication() {
  const { empresaId, isLoading: isEmpresaLoading } = useEmpresa();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoadingQr, setIsLoadingQr] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [instanceName, setInstanceName] = useState<string | null>(null);
  const [requestedNumber, setRequestedNumber] = useState<string | null>(null);
  const [lastRequestAt, setLastRequestAt] = useState<string | null>(null);
  const [lastConnectionCheckAt, setLastConnectionCheckAt] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasShownPollingErrorToastRef = useRef(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("desconectado");

  const statusConfig = useMemo(() => STATUS_CONFIG[connectionStatus], [connectionStatus]);

  const stopConnectionStatePolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    setIsCheckingConnection(false);
  }, []);

  const checkConnectionState = useCallback(
    async (instanceNameToCheck: string) => {
      if (!empresaId) {
        throw new Error("Empresa do usuário não identificada");
      }

      const { data, error } = await supabase.functions.invoke("whatsapp-connection-state", {
        body: {
          instanceName: instanceNameToCheck,
          empresa_id: empresaId,
        },
      });

      if (error) {
        throw new Error(error.message || "Falha ao consultar status da conexão");
      }

      const responseBody: unknown = data;
      const state = extractConnectionStateFromResponse(responseBody);

      if (!state) {
        throw new Error("Resposta da Evolution API sem state válido");
      }

      return state;
    },
    [empresaId],
  );

  const startConnectionStatePolling = useCallback(
    (instanceNameToCheck: string) => {
      stopConnectionStatePolling();
      setIsCheckingConnection(true);
      hasShownPollingErrorToastRef.current = false;

      const poll = async () => {
        try {
          const state = await checkConnectionState(instanceNameToCheck);
          setLastConnectionCheckAt(new Date().toISOString());
          setConnectionStatus(EVOLUTION_STATE_TO_CONNECTION_STATUS[state]);

          if (state === "connected" || state === "open") {
            toast.success("Instância conectada com sucesso");
            stopConnectionStatePolling();
            return;
          }

          if (state === "close") {
            toast.error("A instância foi encerrada (close). Verificação automática interrompida.");
            stopConnectionStatePolling();
          }
        } catch (error) {
          console.error("Erro ao verificar status da conexão:", error);

          if (!hasShownPollingErrorToastRef.current) {
            hasShownPollingErrorToastRef.current = true;
            toast.error("Não foi possível verificar o status da conexão no momento");
          }
        }
      };

      void poll();

      pollingIntervalRef.current = setInterval(() => {
        void poll();
      }, CONNECTION_POLLING_INTERVAL_MS);

      pollingTimeoutRef.current = setTimeout(() => {
        stopConnectionStatePolling();
        toast.error("Tempo máximo de verificação atingido (30 min)");
      }, CONNECTION_POLLING_MAX_DURATION_MS);
    },
    [checkConnectionState, stopConnectionStatePolling],
  );

  useEffect(() => {
    return () => {
      stopConnectionStatePolling();
    };
  }, [stopConnectionStatePolling]);

  useEffect(() => {
    if (isEmpresaLoading) return;

    if (!empresaId) {
      stopConnectionStatePolling();
      return;
    }

    let isCancelled = false;

    const loadPersistedInstance = async () => {
      const { data, error } = await supabase.functions.invoke("whatsapp-connection-state", {
        body: {
          empresa_id: empresaId,
          persistedOnly: true,
        },
      });

      if (isCancelled) return;

      if (error) {
        console.error("Erro ao carregar instância persistida via Edge Function:", error);
        return;
      }

      if (!data || typeof data !== "object" || Array.isArray(data)) {
        return;
      }

      const payload = data as Record<string, unknown>;
      const instancePayload =
        payload.instance && typeof payload.instance === "object" && !Array.isArray(payload.instance)
          ? (payload.instance as Record<string, unknown>)
          : null;

      const persistedState = extractConnectionStateFromResponse(data);
      if (!persistedState) {
        return;
      }

      const persistedStatus = STORED_STATE_TO_CONNECTION_STATUS[persistedState] || "desconectado";

      const persistedSessionId =
        typeof instancePayload?.id === "number" || typeof instancePayload?.id === "string"
          ? String(instancePayload.id)
          : null;
      const persistedInstanceName = extractInstanceNameFromResponse(data);
      const persistedPhone =
        typeof instancePayload?.phone_number === "string" && instancePayload.phone_number.trim()
          ? instancePayload.phone_number.trim()
          : null;
      const persistedUpdatedAt =
        typeof instancePayload?.updated_at === "string" ? instancePayload.updated_at : null;
      const persistedCreatedAt =
        typeof instancePayload?.created_at === "string" ? instancePayload.created_at : null;
      const persistedQrCode =
        typeof instancePayload?.qr_code === "string" && instancePayload.qr_code.trim()
          ? instancePayload.qr_code
          : null;

      setSessionId(persistedSessionId);
      setInstanceName(persistedInstanceName);
      setRequestedNumber(persistedPhone);
      setLastRequestAt(persistedUpdatedAt || persistedCreatedAt || null);
      setLastConnectionCheckAt(persistedUpdatedAt || null);
      setConnectionStatus(persistedStatus);

      if (persistedStatus === "aguardando_leitura") {
        setQrCodeDataUrl(persistedQrCode ? normalizeQrCodeValue(persistedQrCode) : null);

        if (persistedInstanceName) {
          startConnectionStatePolling(persistedInstanceName);
        }

        return;
      }

      setQrCodeDataUrl(null);
    };

    void loadPersistedInstance();

    return () => {
      isCancelled = true;
    };
  }, [empresaId, isEmpresaLoading, startConnectionStatePolling, stopConnectionStatePolling]);

  const handleRequestQrCode = async () => {
    const normalizedNumber = phoneNumber.replace(/\D/g, "");

    if (!normalizedNumber) {
      toast.error("Informe o número de telefone para iniciar a conexão");
      return;
    }

    if (isEmpresaLoading) {
      toast.error("Aguarde a identificação da empresa antes de solicitar o QR Code");
      return;
    }

    if (!empresaId) {
      toast.error("Não foi possível identificar a empresa do usuário logado");
      return;
    }

    stopConnectionStatePolling();
    setIsLoadingQr(true);
    setConnectionStatus("aguardando_leitura");
    setQrCodeDataUrl(null);

    try {
      const { data: responseBody, error: invokeError } = await supabase.functions.invoke(
        "whatsapp-connect",
        { body: { number: normalizedNumber, empresa_id: empresaId } },
      );

      if (invokeError) {
        const context = (invokeError as Record<string, unknown>).context;
        if (context instanceof Response) {
          try {
            const errBody = await (context as Response).clone().json() as Record<string, unknown>;
            throw new Error(
              typeof errBody?.error === "string" ? errBody.error : invokeError.message,
            );
          } catch (innerErr) {
            if (innerErr instanceof Error && innerErr.message !== invokeError.message) throw innerErr;
          }
        }
        throw new Error(invokeError.message || "Falha ao gerar QR Code");
      }

      const qrCode = extractQrCodeFromResponse(responseBody);
      if (!qrCode) {
        throw new Error("A resposta não retornou um QR Code válido.");
      }

      const resolvedSessionId =
        extractSessionIdFromResponse(responseBody) || `sessao-wa-${Date.now().toString().slice(-6)}`;
      const resolvedInstanceName = extractInstanceNameFromResponse(responseBody) || normalizedNumber;

      setSessionId(resolvedSessionId);
      setInstanceName(resolvedInstanceName);
      setRequestedNumber(normalizedNumber);
      setLastRequestAt(new Date().toISOString());
      setQrCodeDataUrl(normalizeQrCodeValue(qrCode));

      startConnectionStatePolling(resolvedInstanceName);
      toast.success("QR Code gerado com sucesso");
    } catch (error) {
      console.error("Erro ao solicitar QR Code:", error);
      setConnectionStatus("desconectado");

      const message = error instanceof Error ? error.message : "Não foi possível gerar o QR Code.";
      toast.error("Falha ao solicitar QR Code", {
        description: message,
      });
    } finally {
      setIsLoadingQr(false);
    }
  };

  const handleReset = () => {
    stopConnectionStatePolling();
    setConnectionStatus("desconectado");
    setQrCodeDataUrl(null);
    setSessionId(null);
    setInstanceName(null);
    setRequestedNumber(null);
    setLastRequestAt(null);
    setLastConnectionCheckAt(null);
    setIsLoadingQr(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Central de Comunicação"
          subtitle="Configure os canais de envio (WhatsApp, e-mail e outros) em um único painel."
          backTo="/admin/control-center"
        />

        <Tabs defaultValue="whatsapp" className="space-y-4">
          <TabsList>
            <TabsTrigger value="whatsapp" className="gap-2">
              <Smartphone className="h-4 w-4" />
              WhatsApp
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-2">
              <Mail className="h-4 w-4" />
              E-mail
            </TabsTrigger>
          </TabsList>

          <TabsContent value="whatsapp" className="space-y-4">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
              <Card className="rounded-2xl border-border/60 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-slate-600" />
                    Conectar WhatsApp
                  </CardTitle>
                  <CardDescription>
                    Informe o número do WhatsApp para gerar o QR Code de pareamento.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Número do telefone (com DDI)</p>
                    <Input
                      value={phoneNumber}
                      onChange={(event) => setPhoneNumber(event.target.value)}
                      placeholder="Ex: 5511999999999"
                      type="number"
                    />
                    <p className="text-xs text-slate-500">
                      Use apenas números. Exemplo: 5511999999999
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleRequestQrCode} disabled={isLoadingQr || !phoneNumber.trim()} className="gap-2">
                      <QrCode className="h-4 w-4" />
                      {isLoadingQr ? "Solicitando QR Code..." : "Gerar QR Code"}
                    </Button>

                    <Button variant="outline" onClick={handleReset} className="gap-2">
                      <RefreshCcw className="h-4 w-4" />
                      Reiniciar fluxo
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-border/60 shadow-sm">
                <CardHeader>
                  <CardTitle>Conexão WhatsApp</CardTitle>
                  <CardDescription>Status da sessão e QR Code de pareamento</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <statusConfig.Icon className="h-4 w-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">Status atual</span>
                    </div>
                    <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
                  </div>

                  <div className="space-y-1 text-xs text-slate-500">
                    <p>
                      <span className="font-medium text-slate-700">ID da sessão:</span> {sessionId || "-"}
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Nome da instância:</span> {instanceName || "-"}
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Número solicitado:</span> {requestedNumber ? `+${requestedNumber}` : "-"}
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Última requisição:</span> {formatDateTime(lastRequestAt)}
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Última checagem de status:</span> {formatDateTime(lastConnectionCheckAt)}
                    </p>
                  </div>

                  <Separator />

                  {isLoadingQr ? (
                    <div className="flex h-72 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
                      <div className="text-center">
                        <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
                        <p className="text-sm font-medium text-slate-700">Solicitando QR Code...</p>
                        <p className="text-xs text-slate-500">Aguarde alguns segundos</p>
                      </div>
                    </div>
                  ) : qrCodeDataUrl && connectionStatus !== "conectado" ? (
                    <div className="space-y-3">
                      <div className="flex justify-center rounded-xl border border-slate-200 bg-white p-4">
                        <img
                          src={qrCodeDataUrl}
                          alt="QR Code da conexão WhatsApp"
                          className="h-60 w-60 rounded-md border border-slate-100"
                        />
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-center text-xs text-slate-600">
                        {isCheckingConnection
                          ? "Verificando status automaticamente a cada 10 segundos (até 30 minutos)."
                          : "Escaneie o QR Code para concluir a conexão."}
                      </div>
                    </div>
                  ) : connectionStatus === "conectado" ? (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <p className="text-sm font-semibold text-emerald-900">WhatsApp conectado com sucesso</p>
                      <p className="mt-1 text-xs text-emerald-700">
                        A instância está pronta para enviar mensagens automáticas.
                      </p>
                    </div>
                  ) : (
                    <div className="flex h-72 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
                      <div className="max-w-xs text-center">
                        <Smartphone className="mx-auto mb-3 h-8 w-8 text-slate-400" />
                        <p className="text-sm font-medium text-slate-700">Nenhum QR Code disponível</p>
                        <p className="text-xs text-slate-500">
                          Informe um número e clique em "Gerar QR Code" para iniciar a conexão.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="rounded-2xl border-border/60 shadow-sm">
              <CardHeader>
                <CardTitle>Ambiente de Chat WhatsApp</CardTitle>
                <CardDescription>
                  O chat foi separado do fluxo de conexão para melhorar operação e performance.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-600">
                  Acesse o novo ambiente dedicado para visualizar conversas, responder mensagens e executar ações de chat.
                </p>

                <Button asChild>
                  <Link to="/admin/whatsapp-chat">Abrir ambiente de chat</Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="email">
            <Card className="rounded-2xl border-border/60 shadow-sm">
              <CardHeader>
                <CardTitle>Configuração de e-mail</CardTitle>
                <CardDescription>
                  Em breve: SMTP/API provider, templates e regras de envio por evento.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  Esta seção ficará disponível após concluirmos o fluxo de conexão do WhatsApp.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
